import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, type ApiUser } from "./api";

type AuthState =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "authed"; user: ApiUser };

type AuthContextValue = {
  state: AuthState;
  signup: (input: { email: string; password: string; name: string }) => Promise<void>;
  login: (input: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    // /p/:token is an inherently public reviewer surface. Skip the owner
    // session probe entirely so a stale clarity_sid cookie can't bleed the
    // owner's identity into the header on a share link.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/p/")) {
      setState({ status: "anon" });
      return;
    }

    let cancelled = false;
    api
      .me()
      .then((res) => {
        if (cancelled) return;
        setState(res.user ? { status: "authed", user: res.user } : { status: "anon" });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 401 just means anonymous; anything else we still surface as anon
        // but log so dev can spot the network failure.
        if (!(err instanceof ApiError) || err.status !== 401) console.warn("auth: /me failed", err);
        setState({ status: "anon" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signup = useCallback(async (input: { email: string; password: string; name: string }) => {
    const res = await api.signup(input);
    setState({ status: "authed", user: res.user });
  }, []);

  const login = useCallback(async (input: { email: string; password: string }) => {
    const res = await api.login(input);
    setState({ status: "authed", user: res.user });
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setState({ status: "anon" });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, signup, login, logout }),
    [state, signup, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
